import { Controller } from "@hotwired/stimulus";
import { normalizeText, smartMatch } from "../../utils/smart_search";

/**
 * Inventory Report Controller
 * Manages inventory report display and filtering
 */
export default class extends Controller {
  static targets = [
    "discrepancyToggle",
    "allItemsRow",
    "discrepancyRow",
    "approvalButtons",
    "filterInput",
    "categoryFilter",
    "exportButton",
  ];

  connect() {
    // Set up initial view
    this.updateVisibleRows();
  }

  /**
   * Toggle between showing all items or only discrepancies
   * @param {Event} event - Change event
   */
  toggleDiscrepancies(event) {
    this.updateVisibleRows();
  }

  /**
   * Update which rows are visible based on filters
   */
  updateVisibleRows() {
    if (!this.hasDiscrepancyToggleTarget) return;

    const showOnlyDiscrepancies = this.discrepancyToggleTarget.checked;

    // Handle all items vs discrepancies
    if (this.hasAllItemsRowTarget) {
      this.allItemsRowTargets.forEach((row) => {
        row.classList.toggle("hidden", showOnlyDiscrepancies);
      });
    }

    if (this.hasDiscrepancyRowTarget) {
      this.discrepancyRowTargets.forEach((row) => {
        row.classList.remove("hidden");
      });
    }

    // Apply text filter if available
    this.applyTextFilter();

    // Apply category filter if available
    this.applyCategoryFilter();

    // Update approval buttons visibility
    this.updateApprovalButtonsVisibility();
  }

  /**
   * Filter items by text input
   */
  applyTextFilter() {
    if (!this.hasFilterInputTarget) return;

    const filterText = normalizeText(this.filterInputTarget.value);
    if (!filterText) return;

    const rows = this.element.querySelectorAll("tr[data-item-name]");
    rows.forEach((row) => {
      const itemName = row.getAttribute("data-item-name") || "";
      const shouldHide = !smartMatch(itemName, filterText);

      if (shouldHide) {
        row.classList.add("hidden");
      }
    });
  }

  /**
   * Filter items by category
   */
  applyCategoryFilter() {
    if (!this.hasCategoryFilterTarget) return;

    const selectedCategory = this.categoryFilterTarget.value;
    if (!selectedCategory || selectedCategory === "all") return;

    const rows = this.element.querySelectorAll("tr[data-category]");
    rows.forEach((row) => {
      const category = row.getAttribute("data-category");
      const shouldHide =
        selectedCategory !== "all" && category !== selectedCategory;

      if (shouldHide) {
        row.classList.add("hidden");
      }
    });
  }

  /**
   * Filter items when text input changes
   */
  filterByText() {
    this.updateVisibleRows();
  }

  /**
   * Filter items when category selection changes
   */
  filterByCategory() {
    this.updateVisibleRows();
  }

  /**
   * Update approval buttons visibility based on filtered items
   */
  updateApprovalButtonsVisibility() {
    if (!this.hasApprovalButtonsTarget) return;

    // Check if there are any visible discrepancy rows
    const visibleDiscrepancies = Array.from(this.discrepancyRowTargets).filter(
      (row) => !row.classList.contains("hidden")
    );

    this.approvalButtonsTarget.classList.toggle(
      "hidden",
      visibleDiscrepancies.length === 0
    );
  }

  /**
   * Export the inventory report to CSV
   */
  exportReport() {
    // Get all visible rows
    const table = this.element.querySelector("table");
    if (!table) return;

    const headers = Array.from(table.querySelectorAll("thead th")).map((th) =>
      th.textContent.trim()
    );

    const visibleRows = Array.from(
      table.querySelectorAll("tbody tr:not(.hidden)")
    );
    const csvContent = [
      headers.join(","),
      ...visibleRows.map((row) => {
        return Array.from(row.querySelectorAll("td"))
          .map((cell) => `"${cell.textContent.trim().replace(/"/g, '""')}"`)
          .join(",");
      }),
    ].join("\n");

    // Create and download the CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "inventory_report.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
